// SPDX-License-Identifier: GPL-2.0-or-later
// Lobby de salas (OG-069; `Menu_serverList` do original com o master server trocado pela API):
// modo Casual/Ranked, lista atualizada a cada 3 s com jogadores, mapa e ping da região, "Jogar
// agora" por modo, link de convite e criação de sala privada Casual. Visual v2 (OG-203): abas por modo,
// filtros e tabela de salas; convite e criação de sala em formulários do design system. A aba Ranked é a
// lista de salas de lobby (OG-255, `ranked-rooms.tsx`): sem "Jogar agora" no Ranked.
import {
  clampTraining,
  defaultTraining,
  PRESETS,
  TRAINING_LIMITS,
  TRAINING_MAPS,
  TRAINING_MODES_LIST,
  AIM_TRAINING_MAP,
  type TrainingModeId,
} from '@outgun/settings';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { loadManifest } from '../assets.js';
import { joinName, useAccount } from '../app/account.js';
import { deviceId } from '../app/device.js';
import { api, ApiError } from '../app/api.js';
import { usePings, withRegion } from '../app/regions.js';
import { BOT_LEVEL_CHOICES, usePrefs } from '../app/prefs.js';
import { copyText } from '../app/clipboard.js';
import { joinErrorText } from '../game/net/ticket.js';
import { Badge, Button } from '../ui/Button.js';
import { ChoiceField, SliderField, TextField, ToggleField } from '../ui/Field.js';
import { Form, FormMessageLine, Page } from '../ui/Page.js';
import { RegionCell } from '../ui/Flag.js';
import { Table } from '../ui/Table.js';
import { Tabs } from '../ui/Tabs.js';
import { RankedRoomsPanel, WatchButton } from './ranked-rooms.js';
import { MapPreview } from './MapPreview.js';
import { fallbackMs, useLive, useLiveConnected } from '../app/live.js';

type Mode = 'casual' | 'ranked';

export interface RoomSummary {
  id: string;
  name: string;
  mode: Mode;
  preset: string;
  hasPassword: boolean;
  maxPlayers: number;
  players: number;
  /** Sem os bots (OG-282). */
  humans: number;
  map: string | null;
  online: boolean;
  persistent: boolean;
  region: string | null;
  /** Espectador ao vivo (OG-092); null para sala com senha ou fora do ar. */
  spectateUrl?: string | null;
  /** Sala de treino (OG-492): o modo; null nas outras. */
  training?: TrainingModeId | null;
}

/** Nome do modo de treino (OG-492). */
export const TRAINING_MODE_NAMES: Record<TrainingModeId, string> = {
  attack: 'Attack',
  defense: 'Defense',
  escape: 'Escape',
  capture: 'Capture',
  aim: 'Aim',
};
/** O que os bots do outro lado são em cada modo (rótulo da quantidade). */
const TRAINING_BOTS_LABEL: Record<TrainingModeId, string> = {
  attack: 'Defenders',
  defense: 'Attackers',
  escape: 'Chasers',
  capture: 'Maximum escort',
  aim: 'Targets',
};
const TRAINING_HINT: Record<TrainingModeId, string> = {
  attack: 'Get into the defended base and leave the base room with the flag. The bots only defend.',
  defense: 'Hold your flag against a wave of attackers that leave together.',
  escape:
    'Start with the enemy flag in their base and capture it at yours, with chasers on your tail.',
  capture: 'A bot takes your flag home, with an escort: catch the carrier and return the flag.',
  aim: '1 minute shooting the bot caged in the middle of T4: at least 70 shots and 90% accuracy. Hit the goal and you switch sides.',
};
const TRAINING_PREFIX = 'training:';

/** Intervalo da atualização da lista (critério: ≤ 5 s). */
export const REFRESH_MS = 3000;
/** Presets Casual do código, até a lista da API chegar (ela traz também os do painel, OG-365). */
const CASUAL_PRESETS = PRESETS.filter((p) => p.mode === 'casual').map(({ id, description }) => ({
  id,
  description,
}));

/** Filtros do lobby: busca por nome ou mapa e esconder as lotadas. */
export function filterRooms(
  rooms: readonly RoomSummary[],
  search: string,
  hideFull: boolean,
): RoomSummary[] {
  const q = search.trim().toLowerCase();
  return rooms.filter(
    (r) =>
      (!hideFull || !isFull(r)) &&
      (!q || r.name.toLowerCase().includes(q) || (r.map ?? '').toLowerCase().includes(q)),
  );
}

// bots não ocupam vaga: o servidor tira um para o humano entrar (OG-445)
const isFull = (r: RoomSummary) => r.online && r.humans >= r.maxPlayers;

/** Ordem da lista: com gente primeiro (mais cheia antes), depois por nome; lotadas no fim. */
export function sortRooms(rooms: readonly RoomSummary[]): RoomSummary[] {
  const full = (r: RoomSummary) => (isFull(r) ? 1 : 0);
  return [...rooms].sort(
    (a, b) => full(a) - full(b) || b.players - a.players || a.name.localeCompare(b.name),
  );
}

/**
 * Salas da região mais próxima primeiro (OG-546), mantendo a ordem de `sortRooms` dentro de cada
 * região; com uma região só (ou sem ping), a lista fica como está.
 */
export function nearestFirst(rooms: readonly RoomSummary[], pings: ReadonlyMap<string, number>): RoomSummary[] {
  const rank = (r: RoomSummary) => (r.region !== null && pings.has(r.region) ? pings.get(r.region)! : Infinity);
  return [...rooms].sort((a, b) => (isFull(a) ? 1 : 0) - (isFull(b) ? 1 : 0) || rank(a) - rank(b));
}

/** "Jogar agora": a API escolhe a sala do modo; o jogo pede o próprio ticket ao entrar. */
export async function quickJoin(mode: Mode, name: string): Promise<string> {
  const r = await api<{ roomId: string }>('/rooms/quick-join', {
    body: await withRegion({ mode, name, device: deviceId() }),
  });
  return r.roomId;
}

/** Nome do jogador: o da conta, ou um campo para o convidado (vai para as preferências). */
function NameField() {
  const { t, prefs, setPrefs } = usePrefs();
  const { user } = useAccount();
  return user ? (
    <TextField label={t('Name')} value={user.username} onChange={() => {}} disabled />
  ) : (
    <TextField
      label={t('Name')}
      value={prefs.playerName}
      onChange={(v) => setPrefs({ playerName: v })}
      maxLength={15}
    />
  );
}

export function LobbyScreen() {
  const { t, prefs } = usePrefs();
  const { user } = useAccount();
  const navigate = useNavigate();
  const pings = usePings();
  // "Ranked" abre a lista das salas de lobby (OG-255); a volta da criação de sala Ranked cai nela
  const location = useLocation();
  const [mode, setMode] = useState<Mode>(() =>
    (location.state as { mode?: Mode } | null)?.mode === 'ranked' ? 'ranked' : 'casual',
  );
  const [rooms, setRooms] = useState<RoomSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [hideFull, setHideFull] = useState(false);
  const name = joinName(user, prefs.playerName);
  // portal ao vivo (OG-284): sala mudou, relê na hora; a consulta fica de reserva
  const [changed, setChanged] = useState(0);
  useLive('rooms', () => setChanged((n) => n + 1));
  const every = fallbackMs(REFRESH_MS, useLiveConnected());

  useEffect(() => {
    if (mode !== 'casual') return;
    let alive = true;
    const load = () =>
      api<{ rooms: RoomSummary[] }>(`/rooms?mode=casual`).then(
        (r) => {
          if (!alive) return;
          setRooms(sortRooms(r.rooms));
          setError(null);
        },
        () => alive && setError(t('Could not reach the server.')),
      );
    void load();
    const id = setInterval(() => void load(), every);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [mode, t, every, changed]);

  const fail = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : 'unknown';
    if (code === 'account_required' || code === 'beta_account_required') void navigate('/login');
    else setError(t(code === 'no_room' ? 'No room available right now.' : joinErrorText(code)));
  };
  const enter = (room: RoomSummary) => {
    if (isFull(room)) return;
    if (room.hasPassword) return void navigate(`/rooms/${room.id}`);
    void navigate(`/play?sala=${encodeURIComponent(room.id)}`);
  };
  const playNow = () =>
    void quickJoin('casual', name).then(
      (id) => void navigate(`/play?sala=${encodeURIComponent(id)}`),
      fail,
    );
  const shown = filterRooms(nearestFirst(rooms ?? [], pings), search, hideFull);

  return (
    <Page title={t('Rooms')} onBack={() => void navigate('/')}>
      <Tabs
        label={t('Mode')}
        value={mode}
        onChange={setMode}
        tabs={[
          { id: 'casual', label: t('Casual') },
          { id: 'ranked', label: t('Ranked') },
        ]}
      >
        {mode === 'ranked' ? (
          <RankedRoomsPanel pings={pings} />
        ) : (
          <div className="ui-stack lobby">
            <p className="ui-muted">
              {t('Casual: power-ups on, guests welcome, does not count for the ranking.')}
            </p>
            <div className="lobby-actions">
              {!user && (
                <div className="lobby-name">
                  <NameField />
                </div>
              )}
              <Button variant="primary" size="lg" onClick={playNow}>
                {t('Play now')}
              </Button>
              <Button onClick={() => void navigate(user ? '/rooms/new' : '/login')}>
                {t('Create private room')}
                {!user && <Badge>{t('needs an account')}</Badge>}
              </Button>
            </div>
            {error && <FormMessageLine message={{ text: error, error: true }} />}
            <div className="lobby-filters">
              <TextField
                label={t('Search rooms')}
                type="search"
                value={search}
                onChange={setSearch}
                placeholder={t('Room or map')}
                maxLength={40}
              />
              <ToggleField label={t('Hide full rooms')} checked={hideFull} onChange={setHideFull} />
            </div>
            <Table
              caption={t('Rooms')}
              rows={shown}
              rowKey={(r) => r.id}
              onRowActivate={enter}
              empty={rooms === null ? t('Loading...') : t('No rooms open. "Play now" opens one.')}
              columns={[
                {
                  key: 'name',
                  header: t('Room'),
                  cell: (r) => (
                    <span className="lobby-room">
                      {r.name}
                      {r.training && (
                        <Badge tone="accent">
                          {t('Training - $1', t(TRAINING_MODE_NAMES[r.training]))}
                        </Badge>
                      )}
                      {r.hasPassword && <Badge>{t('Password')}</Badge>}
                      {isFull(r) && <Badge tone="warning">{t('Full')}</Badge>}
                    </span>
                  ),
                },
                { key: 'map', header: t('Map'), cell: (r) => r.map ?? '—' },
                {
                  key: 'players',
                  header: t('Players'),
                  cell: (r) => `${r.players}/${r.maxPlayers}`,
                  align: 'end',
                },
                {
                  key: 'ping',
                  header: t('Ping'),
                  cell: (r) => (
                    <RegionCell region={r.region}>
                      {r.region && pings.has(r.region) ? `${pings.get(r.region)} ms` : '—'}
                    </RegionCell>
                  ),
                  align: 'end',
                },
                {
                  key: 'watch',
                  header: t('Watch'),
                  cell: (r) => <WatchButton room={r} />,
                  align: 'end',
                },
              ]}
            />
          </div>
        )}
      </Tabs>
    </Page>
  );
}

/** Página do link de convite: mostra a sala e pede a senha, se houver. */
export function JoinRoomScreen() {
  const { t } = usePrefs();
  const { user } = useAccount();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [room, setRoom] = useState<RoomSummary | null | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ room: RoomSummary }>(`/rooms/${encodeURIComponent(id)}`).then(
      (r) => alive && setRoom(r.room),
      () => alive && setRoom(null),
    );
    return () => {
      alive = false;
    };
  }, [id]);
  if (room === undefined) return null;
  if (room === null) return <Navigate to="/rooms" replace />;
  const play = () => {
    if (room.mode === 'ranked' && !user) return void navigate('/login');
    void navigate(
      `/play?sala=${encodeURIComponent(room.id)}`,
      room.hasPassword ? { state: { password } } : undefined,
    );
  };
  const copy = () => void copyText(`${location.origin}/rooms/${room.id}`).then(setCopied);
  return (
    <Page
      title={room.name}
      subtitle={`${room.mode === 'ranked' ? t('Ranked') : t('Casual')} · ${t('Players')} ${room.players}/${room.maxPlayers}${room.map ? ` · ${room.map}` : ''}`}
      onBack={() => void navigate('/rooms')}
      width="narrow"
    >
      <Form
        label={t('Join')}
        onSubmit={play}
        actions={
          <>
            <Button type="submit" variant="primary">
              {t('Join')}
            </Button>
            {room.spectateUrl && (
              <Button
                onClick={() =>
                  void navigate(`/spectate?src=${encodeURIComponent(room.spectateUrl!)}`)
                }
              >
                {t('Watch live')}
              </Button>
            )}
            <Button variant="ghost" onClick={copy}>
              {copied === null
                ? t('Copy invite link')
                : copied
                  ? t('Link copied')
                  : t('Could not copy')}
            </Button>
          </>
        }
      >
        <NameField />
        {room.hasPassword && (
          <TextField
            label={t('Password')}
            type="password"
            value={password}
            onChange={setPassword}
            maxLength={15}
            autoFocus
          />
        )}
      </Form>
    </Page>
  );
}

/** Sala privada Casual (RN-CFG-03): preset, senha, vagas e rotação própria. */
export function CreateRoomScreen() {
  const { t, prefs } = usePrefs();
  const { user } = useAccount();
  const navigate = useNavigate();
  const [name, setName] = useState(() => (user ? `${user.username}` : ''));
  const [preset, setPreset] = useState('classic');
  const [password, setPassword] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(16);
  const [maps, setMaps] = useState<readonly string[]>([]);
  // bots da sala (OG-289): começam com os das opções; desligado, valem os do preset
  const [customBots, setCustomBots] = useState(true);
  const [bots, setBots] = useState(prefs.casualBots);
  const [botLevel, setBotLevel] = useState(prefs.casualBotLevel);
  const [allMaps, setAllMaps] = useState<readonly string[]>([]);
  // atalho 2x2 (OG-519, D-88): 4 vagas e os mapas da rotação 2x2
  const [duoMaps, setDuoMaps] = useState<readonly string[]>([]);
  const [duo, setDuo] = useState(false);
  const pickDuo = (on: boolean) => {
    setDuo(on);
    setMaxPlayers(on ? 4 : 16);
    setMaps(on ? duoMaps : []);
  };
  // miniaturas só com a lista aberta (cada uma lê o mapa)
  const [mapsOpen, setMapsOpen] = useState(false);
  const [presets, setPresets] =
    useState<readonly { id: string; description: string }[]>(CASUAL_PRESETS);
  const [error, setError] = useState<string | null>(null);
  // presets de treino (OG-492, D-86/D-87): mapas de treino, quantidades do modo, nível, power-ups e início
  const trainingMode = preset.startsWith(TRAINING_PREFIX)
    ? (preset.slice(TRAINING_PREFIX.length) as TrainingModeId)
    : null;
  const [training, setTraining] = useState(() => defaultTraining('attack'));
  const [trainingMap, setTrainingMap] = useState(TRAINING_MAPS[0]!);
  const pickPreset = (v: string) => {
    setPreset(v);
    if (v.startsWith(TRAINING_PREFIX))
      setTraining(defaultTraining(v.slice(TRAINING_PREFIX.length) as TrainingModeId));
  };
  useEffect(() => {
    // presets do jogo e do painel (OG-365); mapas só da rotação Casual do painel, se houver (D-69)
    api<{ presets: { id: string; description: string }[] }>('/presets').then(
      (r) => setPresets(r.presets),
      () => {},
    );
    // sem rotação: os mapas do jogo + os publicados no painel (OG-377)
    void Promise.all([
      api<{ maps: string[] | null; published?: string[]; duo?: string[] }>('/rotation').catch(
        () => ({
          maps: null,
          published: [],
          duo: [],
        }),
      ),
      loadManifest(),
    ]).then(([rotation, m]) => {
      setDuoMaps(rotation.duo ?? []);
      setAllMaps(
        rotation.maps ?? [
          ...new Set([
            ...(m
              ? Object.entries(m.maps)
                  .filter(([, v]) => v.dir === 'maps')
                  .map(([k]) => k)
              : []),
            ...(rotation.published ?? []),
          ]),
        ],
      );
    });
  }, []);
  // rascunhos de mapa (OG-562): só o moderador vê e escolhe; os convidados entram e jogam normalmente
  const [drafts, setDrafts] = useState<readonly { name: string; title: string; revision: number }[]>([]);
  const moderator = !!(user?.isModerator || user?.isAdmin);
  useEffect(() => {
    if (!moderator) return setDrafts([]);
    api<{ drafts: { name: string; title: string; revision: number }[] }>('/admin/maps/drafts').then(
      (r) => setDrafts(r.drafts),
      () => setDrafts([]),
    );
  }, [moderator]);
  const duel = preset === 'duel';
  const slots = duel ? 2 : maxPlayers;
  // na sala 2x2 os mapas da rotação 2x2 também aparecem na lista
  const mapChoices = [...(duo ? [...allMaps, ...duoMaps.filter((m) => !allMaps.includes(m))] : allMaps), ...drafts.map((d) => d.name)];
  const draftLabel = (m: string) => {
    const d = drafts.find((x) => x.name === m);
    return d ? `${d.title} (${t('draft')} ${d.revision})` : m;
  };
  const mapsLabel = useMemo(
    () =>
      maps.length === 0
        ? t('Official rotation')
        : maps.length === 1
          ? maps[0]!
          : t('$1 maps', String(maps.length)),
    [maps, t],
  );
  if (user === null) return <Navigate to="/login" replace />;

  const create = () => {
    if (!name.trim()) return;
    setError(null);
    const body = trainingMode
      ? {
          name: name.trim(),
          preset: 'classic',
          ...(password ? { password } : {}),
          // Treino de mira (OG-569): mapa fixo T4
          maps: [trainingMode === 'aim' ? AIM_TRAINING_MAP : trainingMap],
          training: clampTraining({ ...training, mode: trainingMode }),
        }
      : {
          name: name.trim(),
          preset,
          ...(password ? { password } : {}),
          ...(duel ? {} : { maxPlayers: duo ? 4 : maxPlayers }),
          ...(maps.length ? { maps } : {}),
          ...(customBots ? { bots: { count: Math.min(bots, slots - 1), level: botLevel } } : {}),
        };
    void withRegion(body)
      .then((nearest) => api<{ room: RoomSummary; inviteUrl: string }>('/rooms', { body: nearest }))
      .then(
      ({ room }) =>
        void navigate(
          `/play?sala=${encodeURIComponent(room.id)}`,
          password ? { state: { password } } : undefined,
        ),
      (e: unknown) =>
        setError(
          e instanceof ApiError && e.code === 'rate_limited'
            ? t('Too many rooms. Try again later.')
            : e instanceof ApiError && e.code === 'training_room_open'
              ? t('You already have a training room open. Leave it before creating another.')
              : t('Could not create the room.'),
        ),
    );
  };
  const toggleMap = (m: string) =>
    setMaps((list) => (list.includes(m) ? list.filter((x) => x !== m) : [...list, m]));
  return (
    <Page title={t('Create private room')} onBack={() => void navigate('/rooms')} width="narrow">
      <Form
        label={t('Create private room')}
        onSubmit={create}
        actions={
          <Button type="submit" variant="primary" disabled={!name.trim()}>
            {t('Create and play')}
          </Button>
        }
      >
        <TextField
          label={t('Room name')}
          value={name}
          onChange={setName}
          maxLength={40}
          autoFocus
        />
        <ChoiceField
          label={t('Rules')}
          value={preset}
          onChange={pickPreset}
          // nome curto do preset: o começo da descrição ("Clássico: ..."); depois o grupo Treino
          options={[
            ...presets.map((p) => ({ value: p.id, label: p.description.split(':')[0] || p.id })),
            ...TRAINING_MODES_LIST.map((m) => ({
              value: `${TRAINING_PREFIX}${m}`,
              label: t('Training - $1', t(TRAINING_MODE_NAMES[m])),
            })),
          ]}
          hint={
            trainingMode
              ? t(TRAINING_HINT[trainingMode])
              : presets.find((p) => p.id === preset)?.description
          }
        />
        <TextField
          label={t('Password (optional)')}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          maxLength={15}
        />
        {trainingMode === 'aim' ? (
          // Treino de mira (OG-569): T4 fixo, um alvo, sem nível nem power-ups; só o início
          <ToggleField
            label={t('Same start every attempt')}
            checked={training.sameStart}
            onChange={(v) => setTraining({ ...training, sameStart: v })}
            hint={t('The aim training is solo: no guests in this room.')}
          />
        ) : trainingMode ? (
          <>
            <ChoiceField
              label={t('Map')}
              value={trainingMap}
              onChange={setTrainingMap}
              options={TRAINING_MAPS.map((m) => ({ value: m, label: m }))}
            />
            <SliderField
              label={t(TRAINING_BOTS_LABEL[trainingMode])}
              value={training.bots}
              min={TRAINING_LIMITS[trainingMode].bots[0]}
              max={TRAINING_LIMITS[trainingMode].bots[1]}
              onChange={(v) => setTraining({ ...training, bots: v })}
            />
            {trainingMode === 'defense' && (
              <SliderField
                label={t('Allied bots')}
                value={training.allies}
                min={0}
                max={TRAINING_LIMITS.defense.allies[1]}
                onChange={(v) => setTraining({ ...training, allies: v })}
              />
            )}
            <ChoiceField
              label={t('Bot level')}
              value={String(training.level)}
              options={[
                ...BOT_LEVEL_CHOICES.filter(([v]) => v > 0).map(([value, text]) => ({
                  value: String(value),
                  label: t(text),
                })),
                { value: '0', label: t('progressive (goes up as you succeed)') },
              ]}
              onChange={(v) => setTraining({ ...training, level: Number(v) })}
            />
            <ToggleField
              label={t('Power-ups')}
              checked={training.powerups}
              onChange={(v) => setTraining({ ...training, powerups: v })}
            />
            <ToggleField
              label={t('Same start every attempt')}
              checked={training.sameStart}
              onChange={(v) => setTraining({ ...training, sameStart: v })}
              hint={
                training.sameStart
                  ? t('Good for drilling one specific play.')
                  : t('Each attempt starts a little differently.')
              }
            />
          </>
        ) : (
          <>
            {!duel && (
              <ToggleField
                label={t('2x2')}
                checked={duo}
                onChange={pickDuo}
                hint={duo ? t('4 players and the small maps of the 2x2 rotation.') : undefined}
              />
            )}
            <SliderField
              label={t('Players')}
              value={duel ? 2 : maxPlayers}
              min={2}
              max={32}
              step={2}
              disabled={duel || duo}
              onChange={setMaxPlayers}
            />
            <ToggleField
              label={t('Choose the bots')}
              checked={customBots}
              onChange={setCustomBots}
              hint={customBots ? undefined : t('The rules decide the bots.')}
            />
            <SliderField
              label={t('Number of bots')}
              value={Math.min(bots, slots - 1)}
              min={0}
              max={slots - 1}
              disabled={!customBots}
              onChange={setBots}
            />
            <ChoiceField
              label={t('Bot level')}
              value={String(botLevel)}
              disabled={!customBots}
              options={BOT_LEVEL_CHOICES.map(([value, text]) => ({
                value: String(value),
                label: t(text),
              }))}
              onChange={(v) => setBotLevel(Number(v))}
            />
          </>
        )}
      </Form>
      {trainingMode && trainingMode !== 'aim' && <MapPreview name={trainingMap} label={trainingMap} thumb />}
      {!trainingMode && (
        <details className="lobby-maps" onToggle={(e) => setMapsOpen(e.currentTarget.open)}>
          <summary>
            {t('Maps')}: {mapsLabel}
          </summary>
          <div className="ui-row">
            <Button variant="ghost" onClick={() => setMaps([])} disabled={maps.length === 0}>
              {t('Official rotation')}
            </Button>
          </div>
          <ul>
            {mapChoices.map((m) => (
              <li key={m}>
                <label>
                  <input type="checkbox" checked={maps.includes(m)} onChange={() => toggleMap(m)} />
                  {mapsOpen && <MapPreview name={m.replace(/~r\d+$/, '')} label={m} thumb revision={drafts.find((d) => d.name === m)?.revision} />}
                  {draftLabel(m)}
                </label>
              </li>
            ))}
          </ul>
        </details>
      )}
      {error && <FormMessageLine message={{ text: error, error: true }} />}
    </Page>
  );
}
